import * as React from 'react'
import { View } from 'react-native'
import styled from '@theme/styled-components'

import TextInput from '@components/textInput/TextInput'
import Choice from '@components/choice/Choice'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'

import * as SiteStore from '@store/site/site'
import * as IncidentStore from '@store/incident/incident'

import { FormikHelpers, useFormik } from 'formik'

import sanitize from '@utils/Sanitizer'

interface Values {
  siteId: string
}

const Site = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(() => ({ siteId: form.siteId }), [form.siteId])

  const i18n = useI18n()
  const sites = useReducer(SiteStore.store, (s) => s.sites)

  const [search, setSearch] = React.useState('')

  const items = React.useMemo(
    () =>
      sites
        .filter((s) => sanitize(i18n.t(`screens.site.sites.${s.codeLieu}`)).includes(sanitize(search)))
        .sort((a, b) =>
          i18n.t(`screens.site.sites.${a.codeLieu}`).localeCompare(i18n.t(`screens.site.sites.${b.codeLieu}`))
        ),
    [sites, search]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    if (vals.siteId !== form.siteId) {
      IncidentStore.actions.resetForm()
    }
    onForm(vals, true).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const onSelect = (val: string) => {
    if (values.siteId !== val) {
      setFieldValue('siteId', val)
    }
  }

  const options = items.map((b) => ({ label: i18n.t(`screens.site.sites.${b.codeLieu}`), value: b.id }))
  const opt = options.find((o) => values.siteId === o.value)

  return (
    <MainContainer>
      <SearchContainer>
        <TextInput
          value={search}
          onChange={setSearch}
          placeholder={i18n.t('screens.incident.SITE.search')}
          left={{ name: 'search' }}
        />
      </SearchContainer>

      <Choice data={options} onItemSelected={(next) => onSelect(next.value as string)} value={opt} />
    </MainContainer>
  )
}

export default Site

const MainContainer = styled(View)`
  margin: 0 20px;
`

const SearchContainer = styled(View)`
  margin-top: 50px;
  margin-bottom: 25px;
`
